import { AssetHierarchy } from '../models/asset-hierarchy.model';
import { Asset } from '../models/asset.model';

/**
 * Domain service for building asset hierarchies from flat data
 * Pure TypeScript, no framework dependencies
 */
export class HierarchyBuilderService {
  /**
   * Build hierarchy from flat array of assets
   * Constructs parent-child relationships based on parentId
   */
  buildFromFlat(assets: Asset[]): AssetHierarchy {
    if (assets.length === 0) {
      return new AssetHierarchy([]);
    }

    // Create lookup map
    const assetMap = new Map<string, Asset>();
    assets.forEach((asset) => assetMap.set(asset.id, asset));

    // Separate roots from children
    const roots: Asset[] = [];
    const childrenByParentId = new Map<string, Asset[]>();

    for (const asset of assets) {
      if (asset.isRoot()) {
        roots.push(asset);
      } else if (asset.parentId) {
        const siblings = childrenByParentId.get(asset.parentId) || [];
        siblings.push(asset);
        childrenByParentId.set(asset.parentId, siblings);
      }
    }

    // Clear stale children before re-attaching (assets are mutated objects reused across recomputes)
    for (const asset of assetMap.values()) {
      asset.clearChildren();
    }

    // Attach children to parents
    for (const [parentId, children] of childrenByParentId.entries()) {
      const parent = assetMap.get(parentId);
      if (parent) {
        parent.addChildren(children);
      }
    }

    return new AssetHierarchy(roots);
  }

  /**
   * Build hierarchy with sorting
   * Sorts assets by name at each level
   */
  buildSorted(
    assets: Asset[],
    compareFn?: (a: Asset, b: Asset) => number,
  ): AssetHierarchy {
    const defaultCompare = (a: Asset, b: Asset) => a.name.localeCompare(b.name);
    const compare = compareFn || defaultCompare;

    // Sort assets
    const sortedAssets = [...assets].sort(compare);

    // Build hierarchy
    const hierarchy = this.buildFromFlat(sortedAssets);

    // Sort children recursively
    this.sortChildren(hierarchy.getRoots(), compare);

    return hierarchy;
  }

  /**
   * Recursively sort children at all levels
   */
  private sortChildren(
    assets: ReadonlyArray<Asset>,
    compareFn: (a: Asset, b: Asset) => number,
  ): void {
    for (const asset of assets) {
      if (asset.hasChildren()) {
        const sortedChildren = Array.from(asset.children).sort(compareFn);
        // Clear and re-add in sorted order
        while (asset.children.length > 0) {
          const child = asset.children[0];
          asset.removeChild(child.id);
        }
        asset.addChildren(sortedChildren);

        // Recursively sort grandchildren
        this.sortChildren(asset.children, compareFn);
      }
    }
  }

  /**
   * Merge two hierarchies
   * Useful for incremental loading
   */
  merge(existing: AssetHierarchy, newAssets: Asset[]): AssetHierarchy {
    const allAssets = [...existing.getAllAssets(), ...newAssets];

    // Deduplicate by ID
    const uniqueAssets = Array.from(
      new Map(allAssets.map((asset) => [asset.id, asset])).values(),
    );

    return this.buildFromFlat(uniqueAssets);
  }

  /**
   * Filter hierarchy by predicate
   * Returns new hierarchy with only matching assets and their ancestors
   */
  filter(
    hierarchy: AssetHierarchy,
    predicate: (asset: Asset) => boolean,
  ): AssetHierarchy {
    const matching = hierarchy.getAllAssets().filter(predicate);
    const toInclude = new Set<string>();

    // Include matching assets and all their ancestors
    for (const asset of matching) {
      toInclude.add(asset.id);
      const ancestors = hierarchy.getAncestors(asset);
      ancestors.forEach((ancestor) => toInclude.add(ancestor.id));
    }

    // Filter to included assets
    const filteredAssets = hierarchy
      .getAllAssets()
      .filter((asset) => toInclude.has(asset.id));

    return this.buildFromFlat(filteredAssets);
  }
}
